import React, { useState } from 'react';
import { Smartphone, Download, CheckCircle, AlertCircle, Apple, Smartphone as Android } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

type Platform = 'android' | 'ios';

const MobileAppDownload: React.FC = () => {
  const [downloadError] = React.useState<string | null>(null);
  const [selectedPlatform, setSelectedPlatform] = useState<Platform>('android');

  // Android download URL - Google Play Store
  const androidDownloadUrl = 'https://play.google.com/store/apps/details?id=com.complin';
  
  // iOS App Store URL
  const iosAppStoreUrl = 'https://apps.apple.com/us/app/complin-ai/id6755016084';
  
  // Generate QR code for mobile download
  const qrCodeUrl = selectedPlatform === 'android' ? androidDownloadUrl : iosAppStoreUrl;

  // For desktop users who want to download directly
  const handleDownload = () => {
    if (selectedPlatform === 'android') {
      window.open(androidDownloadUrl, '_blank');
    } else {
      window.open(iosAppStoreUrl, '_blank');
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-8 text-center">
          <Smartphone className="w-16 h-16 mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-2">ComplinAI Mobile App</h1>
          <p className="text-blue-100">Download the mobile app for Android or iOS</p>
        </div>

        {/* Platform Selection Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              onClick={() => setSelectedPlatform('android')}
              className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
                selectedPlatform === 'android'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Android className="w-5 h-5" />
                <span>Android</span>
              </div>
            </button>
            <button
              onClick={() => setSelectedPlatform('ios')}
              className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
                selectedPlatform === 'ios'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Apple className="w-5 h-5" />
                <span>iOS</span>
              </div>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Features */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Features</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-medium">Checklist Management</h3>
                  <p className="text-sm text-gray-600">Complete checklists on the go with photo attachments</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-medium">Asset Tracking</h3>
                  <p className="text-sm text-gray-600">View and manage assets by location and type</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-medium">Form Completion</h3>
                  <p className="text-sm text-gray-600">Fill out forms directly from your mobile device</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-medium">Activity History</h3>
                  <p className="text-sm text-gray-600">View past completions and activity for assets</p>
                </div>
              </div>
            </div>
          </div>

          {/* QR Code Download Section */}
          <div className="border-t border-gray-200 pt-8">
            <h2 className="text-xl font-semibold mb-4">Download Instructions</h2>
            
            {/* QR Code Section */}
            <div className="mb-8 p-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <div className="flex flex-col md:flex-row items-center justify-center gap-8">
                {/* QR Code */}
                <div className="flex flex-col items-center">
                  <div className="bg-white p-4 rounded-lg shadow-md">
                    <QRCodeSVG
                      value={qrCodeUrl}
                      size={200}
                      level="H"
                      includeMargin={true}
                    />
                  </div>
                  <p className="mt-4 text-sm text-gray-600 text-center max-w-xs">
                    {selectedPlatform === 'android' 
                      ? 'Scan this QR code with your Android device camera'
                      : 'Scan this QR code with your iPhone or iPad camera'}
                  </p>
                </div>

                {/* Instructions */}
                <div className="flex-1 max-w-md">
                  <h3 className="font-semibold mb-3 text-lg">How to Download:</h3>
                  {selectedPlatform === 'android' ? (
                    <ol className="list-decimal list-inside space-y-2 text-gray-700">
                      <li>Open your Android device camera</li>
                      <li>Point it at the QR code above</li>
                      <li>Tap the notification when the link appears</li>
                      <li>You'll be redirected to the Google Play Store</li>
                      <li>Tap "Install" to download and install the app</li>
                    </ol>
                  ) : (
                    <ol className="list-decimal list-inside space-y-2 text-gray-700">
                      <li>Open your iPhone or iPad camera</li>
                      <li>Point it at the QR code above</li>
                      <li>Tap the notification when it appears</li>
                      <li>You'll be redirected to the App Store</li>
                      <li>Tap "Get" or the download button to install</li>
                    </ol>
                  )}
                </div>
              </div>
            </div>

            {/* Alternative: Direct Download Link for Desktop */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800 mb-3">
                <strong>Desktop Users:</strong>{' '}
                {selectedPlatform === 'android' 
                  ? 'Click the button below to open the Google Play Store. You can install the app on your Android device from there.'
                  : 'Click the button below to open the App Store on your iOS device or in iTunes.'}
              </p>
              <button
                onClick={handleDownload}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                {selectedPlatform === 'android' ? (
                  <>
                    <Android className="w-4 h-4" />
                    <span>Open Google Play Store</span>
                  </>
                ) : (
                  <>
                    <Apple className="w-4 h-4" />
                    <span>Open App Store</span>
                  </>
                )}
              </button>
            </div>

            {/* Installation Steps */}
            <div className="mb-6">
              <h3 className="font-semibold mb-3">Installation Steps:</h3>
              {selectedPlatform === 'android' ? (
                <ol className="list-decimal list-inside space-y-2 text-gray-700">
                  <li>Open the Google Play Store link on your Android device</li>
                  <li>Tap "Install" to download and install the app</li>
                  <li>Wait for the installation to complete</li>
                  <li>Launch the app from your home screen and sign in with your ComplinAI credentials</li>
                </ol>
              ) : (
                <ol className="list-decimal list-inside space-y-2 text-gray-700">
                  <li>Tap "Get" or the download button on the App Store page</li>
                  <li>Authenticate with Face ID, Touch ID, or your Apple ID password if prompted</li>
                  <li>Wait for the installation to complete</li>
                  <li>Launch the app from your home screen and sign in with your ComplinAI credentials</li>
                </ol>
              )}
            </div>

            {/* Error Message */}
            {downloadError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-red-800">Download Error</h3>
                  <p className="text-sm text-red-600 mt-1">{downloadError}</p>
                  <p className="text-sm text-red-600 mt-2">
                    The app file may not be available yet. Please contact your administrator.
                  </p>
                </div>
              </div>
            )}

            {/* System Requirements */}
            <div className="mt-8 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium mb-2">System Requirements</h3>
              {selectedPlatform === 'android' ? (
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Android 7.0 (API level 24) or higher</li>
                  <li>• Internet connection required</li>
                  <li>• Google account for sign-in</li>
                </ul>
              ) : (
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• iOS 18.5 or later</li>
                  <li>• iPhone, iPad, or Mac with Apple M1 chip or later</li>
                  <li>• Internet connection required</li>
                  <li>• Apple ID for App Store download</li>
                  <li>• Google account or email for sign-in</li>
                </ul>
              )}
            </div>

            {/* Security Note */}
            {selectedPlatform === 'android' && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="font-medium text-green-900 mb-2">Google Play Store Verified</h3>
                <p className="text-sm text-green-800">
                  This app is available on the official Google Play Store and has been reviewed and approved by Google. 
                  It is safe and secure to download and install.
                </p>
              </div>
            )}
            {selectedPlatform === 'ios' && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="font-medium text-green-900 mb-2">App Store Verified</h3>
                <p className="text-sm text-green-800">
                  This app is available on the official Apple App Store and has been reviewed and approved by Apple. 
                  It is safe and secure to download and install.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileAppDownload;

